import { PALETTE, SKILL_BY_ID, type SkillShape } from "@eldrun/content";
import type { SkillEvent } from "@eldrun/protocol";
import { Color, PlaneGeometry, Quaternion, Vector3, type Object3D } from "three";
import type { EnergyEvents } from "../render/energy-events.ts";
import { createSkillBatchMaterial, SHAPE_MARKS } from "../render/shaders/skill-mesh.ts";
import { SkillBatch } from "../render/skill-batch.ts";
import type { PassiveFx } from "./passive-fx.ts";
import { domeCrack, reviveColumn, SkillPieces, skillPieces, type PieceEvent, type PieceSpec } from "./skill-pieces.ts";

/** Habilidades que desenham uma onda (as outras, uma zona com contorno). */
const WAVES: ReadonlySet<string> = new Set(["brado", "flecha-sinalizadora", "investida", "salto-do-falcao", "disparo-rompe-nucleo"]);

/**
 * Onda de choque no pós (F94, só no Alto): a queda do Salto do Falcão, a cúpula do Bastião e o tiro do
 * Rompe-Núcleo (na boca, quando a linha sai; a carga não tem). A força (0 a 1, vezes "Efeitos dos aliados"
 * em quem chama), a altura acima do chão (m) e o raio final (m; sem ele, o do evento).
 */
export const SKILL_SHOCK: Readonly<Record<string, { strength: number; lift: number; radius?: number }>> = {
  "salto-do-falcao": { strength: 1, lift: 0.3 },
  bastiao: { strength: 0.7, lift: 0.5 },
  "disparo-rompe-nucleo": { strength: 0.6, lift: 1.1, radius: 4 },
};

/** A onda de choque do evento de habilidade (F94), ou nada. */
export function skillShock(e: PieceEvent): { strength: number; lift: number; radius: number } | undefined {
  const s = SKILL_SHOCK[e.skill];
  if (!s || (e.skill === "disparo-rompe-nucleo" && e.x2 === undefined)) return undefined;
  return { strength: s.strength, lift: s.lift, radius: s.radius ?? e.radius };
}

/** Nos efeitos em linha, um pulso a cada tantos metros (o prisma do Rompe-Núcleo), em sequência. */
const LINE_STEP = 3;
const LINE_DELAY_MS = 12;

/** Altura do chão num ponto. */
type Ground = (x: number, z: number) => number;

/** Um anel, onda, zona ou clarão vivo no chão: só dado, desenhado no lote das zonas. */
interface Live {
  x: number;
  y: number;
  z: number;
  radius: number;
  /** Cor linear e opacidade. */
  r: number;
  g: number;
  b: number;
  alpha: number;
  marks: number;
  wave: boolean;
  start: number;
  ms: number;
  /** Clarão (F77): luz no chão, com teto próprio (`GLOW.max`). */
  glow?: boolean;
  /** Zona persistente: não pode ser expulsa por pulsos cosméticos. */
  zone?: boolean;
  /** Estrela do impacto (Fase 14): de frente para a câmera, no ar. */
  star?: boolean;
  /** De quem e de qual habilidade (para cortar a carga do Rompe-Núcleo quando quem atira cai). */
  owner?: string;
  skill?: string;
}

const tint = new Color();
/** O plano das zonas (normal +y) de frente para a câmera: a câmera girada de +90° em x. */
const STAND = new Quaternion().setFromAxisAngle(new Vector3(1, 0, 0), Math.PI / 2);
const facing = new Quaternion();

/** A estrela do impacto (Fase 14): quantos raios e o teto de vivas (fora das zonas e dos anéis). */
export const STAR = { rays: 4, max: 24 } as const;

/** O chão das zonas: sem `normal`, como as peças e a fita (a normal criaria outra variante do programa). */
const plane = new PlaneGeometry(2, 2).rotateX(-Math.PI / 2).deleteAttribute("normal");

/** Clarão das habilidades com `lightMs` (Sinalizadora, F77): luz branca (o neutro), mais forte à noite; poucos, reaproveitados. */
export const GLOW = { color: PALETTE.neutro.hex, day: 0.35, night: 1, max: 4 } as const;

/**
 * Efeitos das habilidades (F65, doc 09 §3.4): na cor do slot de quem usou e na geometria da classe.
 * Ondas (Brado, Sinalizadora) e zonas aliadas só com contorno (Runa); por dados (`SkillDef.shape`), sem
 * código por habilidade. Quem tem `lightMs` (a Sinalizadora, F77) também acende um clarão no chão. As peças
 * 3D de cada habilidade (F78: domo, lanças, prisma, penas…) vêm de `skillPieces`.
 */
/** Separado das peças 3D e do clarão: suporta quatro feixes simultâneos e suas zonas. */
export const SKILL_RING_CAPACITY = 96;

export class SkillVfx {
  private readonly root: Object3D;
  private readonly live: Live[] = [];
  private readonly pieces: SkillPieces;
  /** Anéis, ondas, zonas e clarões (Fase 12, Gate B): um lote, um draw call, o programa das peças. */
  private readonly zoneMaterial = createSkillBatchMaterial(true);
  private readonly zones: SkillBatch;

  /** As peças 3D (F78); a vitrine da malha só as liga com `habilidades=1`. */
  private readonly withPieces: boolean;

  /** Eventos de energia (pesquisa 11): acendem o chão e a geometria vizinha na cor do dono. */
  private readonly energy: EnergyEvents | undefined;

  private readonly ringCapacity: number;

  constructor(root: Object3D, withPieces = true, energy?: EnergyEvents, ringCapacity = SKILL_RING_CAPACITY) {
    this.root = root;
    this.ringCapacity = ringCapacity;
    this.zones = new SkillBatch(plane, this.zoneMaterial, ringCapacity + GLOW.max + STAR.max);
    root.add(this.zones.mesh);
    this.withPieces = withPieces;
    this.energy = energy;
    this.pieces = new SkillPieces(root);
  }

  /** "Reduzir lampejos" (Fase 12): as peças 3D sem o pico de brilho. */
  set reduceFlashes(on: boolean) {
    this.pieces.reduceFlashes = on;
  }

  /** `alpha`: a força dos efeitos (os dos aliados pela opção, F68); `night`: de 0 (dia) a 1 (noite), para o clarão. */
  spawn(e: SkillEvent, color: string, ground: Ground, now: number, alpha = 1, night = 0): void {
    // Os que acabaram saem antes de contar o teto (o desenho é no `update`).
    for (let i = this.live.length - 1; i >= 0; i--) if ((now - this.live[i]!.start) / this.live[i]!.ms >= 1) this.live.splice(i, 1);
    const def = SKILL_BY_ID.get(e.skill);
    if (!def || e.radius <= 0) return;
    const wave = WAVES.has(e.skill);
    const lightMs = def.params?.["lightMs"] ?? 0;
    if (lightMs > 0) this.glow(e.x, e.z, ground(e.x, e.z), e.radius, lightMs, night, now);
    if (this.withPieces) this.pieces.spawn(skillPieces(e, ground(e.x, e.z)), color, now, alpha);
    // Resposta local (pesquisa 11): o chão e a geometria perto acendem na cor do dono, e voltam suave.
    // Etiquetada por dono e habilidade: `cancel` apaga a luz da carga junto com o anel.
    this.energy?.add(e.x, e.z, e.radius * 1.2, color, now, Math.max(600, e.ms + 300), alpha, now, `${e.by}:${e.skill}`);
    if (e.x2 !== undefined && e.z2 !== undefined) {
      // Linha (F67): pulsos pequenos de quem atirou até o fim, um depois do outro.
      const n = Math.min(32, Math.max(2, Math.ceil(Math.hypot(e.x2 - e.x, e.z2 - e.z) / LINE_STEP)));
      for (let i = 0; i <= n; i++) {
        const x = e.x + ((e.x2 - e.x) * i) / n;
        const z = e.z + ((e.z2 - e.z) * i) / n;
        this.ring(x, z, ground(x, z), e.radius * 1.6, color, def.shape, wave, now + i * LINE_DELAY_MS, e.ms, alpha);
      }
      // O impacto no fim da linha também acende o chão (o começo já acendeu acima). O início é no
      // futuro: a inserção passa o relógio real (`at`) para não expulsar um evento ainda vivo.
      this.energy?.add(e.x2, e.z2, e.radius * 2, color, now + n * LINE_DELAY_MS, 700, alpha, now);
      return;
    }
    this.ring(e.x, e.z, ground(e.x, e.z), e.radius, color, def.shape, wave, now, e.ms, alpha, e.by, e.skill);
  }

  /**
   * O que ainda está vivo de `owner` com a habilidade `skill` sai na hora (Fase 12, Gate C): a carga do
   * Disparo Rompe-Núcleo quando quem atira cai antes do tiro (o servidor não manda o cancelamento).
   */
  cancel(owner: string, skill: string): number {
    const before = this.live.length;
    for (let i = this.live.length - 1; i >= 0; i--) if (this.live[i]!.owner === owner && this.live[i]!.skill === skill) this.live.splice(i, 1);
    // A luz da carga no chão sai junto (só a dela: as outras do mesmo dono continuam).
    this.energy?.cancel(`${owner}:${skill}`);
    return before - this.live.length;
  }

  /** Uma passiva que agiu num dano (Fase 12, Gate C, `passiveFx`): as peças e a onda, na cor dada. */
  passive(fx: PassiveFx, color: string, x: number, z: number, groundY: number, now: number, alpha = 1): void {
    if (this.withPieces) this.pieces.spawn(fx.pieces, color, now, alpha);
    if (fx.wave) this.ring(x, z, groundY, fx.wave.radius, color, fx.wave.shape, true, now, fx.wave.ms, alpha);
    this.energy?.add(x, z, (fx.wave?.radius ?? 1.2) * 1.4, color, now, 500, alpha * 0.8);
  }

  /** Coluna de luz de quem voltou da Lápide (F78), na cor dele. */
  column(x: number, y: number, z: number, color: string, now: number): void {
    this.pieces.spawn([reviveColumn(x, y, z)], color, now);
    this.energy?.add(x, z, 3, color, now, 900);
  }

  /** A cúpula do Bastião trincou onde parou um arremesso (F96), na cor de quem a ergueu. */
  crack(x: number, y: number, z: number, color: string, now: number, alpha = 1): void {
    if (this.withPieces) this.pieces.spawn(domeCrack(x, y, z), color, now, alpha);
    this.energy?.add(x, z, 2.5, color, now, 500, alpha);
  }

  /** Anel de impacto no chão (F68): crítico e abate, na forma da classe de quem bateu. */
  pulse(x: number, z: number, groundY: number, radius: number, color: string, shape: SkillShape, now: number, alpha = 1): void {
    this.ring(x, z, groundY, radius, color, shape, true, now, 320, alpha);
    this.energy?.add(x, z, radius * 1.6, color, now, 420, alpha * 0.8);
  }

  /**
   * Estrela do impacto (Fase 14, a referência): no ponto do golpe, de frente para a câmera, `radius` m,
   * `ms` de vida. Cheio o teto, a mais velha cede lugar. "Reduzir lampejos" é de quem chama (sem estrela).
   */
  flash(x: number, y: number, z: number, radius: number, color: string, now: number, ms = 140, alpha = 1): void {
    if (alpha <= 0) return;
    const stars = this.live.filter((l) => l.star);
    if (stars.length >= STAR.max) this.live.splice(this.live.indexOf(stars[0]!), 1);
    tint.set(color);
    this.live.push({ x, y, z, radius, r: tint.r, g: tint.g, b: tint.b, alpha, marks: STAR.rays, wave: false, start: now, ms, star: true });
  }

  /** Peças 3D soltas (F91: os cristais da pancada do Troll de Pedra), no programa das peças das habilidades. */
  spawnPieces(specs: readonly PieceSpec[], color: string, now: number, alpha = 1): void {
    this.pieces.spawn(specs, color, now, alpha);
  }

  /**
   * Elo (F67): uma onda por participante, na cor e na forma de cada um, em anéis intercalados (sem misturar
   * as cores; doc 09 §2.2, regra 5).
   */
  elo(x: number, z: number, groundY: number, who: readonly { color: string; shape: SkillShape }[], now: number): void {
    who.forEach((w, i) => this.ring(x, z, groundY, 2.2 + i * 0.7, w.color, w.shape, true, now + i * 60, 650));
  }

  /** Ilumina a área (o clarão no chão): é luz, não efeito, então não cai com a opção dos aliados. */
  private glow(x: number, z: number, groundY: number, radius: number, ms: number, night: number, now: number): void {
    if (this.live.filter((l) => l.glow).length >= GLOW.max) return;
    tint.set(GLOW.color);
    const alpha = GLOW.day + (GLOW.night - GLOW.day) * Math.min(1, Math.max(0, night));
    this.live.push({ x, y: groundY + 0.06, z, radius, r: tint.r, g: tint.g, b: tint.b, alpha, marks: 0, wave: false, start: now, ms, glow: true });
  }

  private ring(x: number, z: number, groundY: number, radius: number, color: string, shape: SkillShape, wave: boolean, start: number, ms: number, alpha = 1, owner?: string, skill?: string): void {
    if (alpha <= 0) return;
    if (this.live.length - this.live.filter((l) => l.glow || l.star).length >= this.ringCapacity) {
      // Preserva áreas ainda ativas. Uma zona nova pode tomar o pulso decorativo mais antigo.
      const replace = this.live.findIndex((l) => !l.glow && !l.zone && !l.star);
      if (wave || replace < 0) return;
      this.live.splice(replace, 1);
    }
    tint.set(color);
    this.live.push({ x, y: groundY + 0.08, z, radius, r: tint.r, g: tint.g, b: tint.b, alpha, marks: SHAPE_MARKS[shape], wave, start, ms: Math.max(200, ms), zone: !wave, ...(owner !== undefined && { owner }), ...(skill !== undefined && { skill }) });
  }

  /** `camera`: o giro da câmera, para as estrelas ficarem de frente (sem ele, deitadas como as zonas). */
  update(now: number, camera?: Quaternion): void {
    this.pieces.update(now);
    if (camera) facing.copy(camera).multiply(STAND);
    this.zones.begin();
    for (let i = this.live.length - 1; i >= 0; i--) {
      const l = this.live[i]!;
      const k = (now - l.start) / l.ms;
      if (k >= 1) {
        this.live.splice(i, 1);
        continue;
      }
      if (k < 0) continue;
      if (l.star) {
        const q = camera ? facing : STAND;
        this.zones.push(l.x, l.y, l.z, q.x, q.y, q.z, q.w, l.radius, l.radius, l.radius, l.r, l.g, l.b, l.alpha, k, l.marks, 0, 3);
        continue;
      }
      this.zones.push(l.x, l.y, l.z, 0, 0, 0, 1, l.radius, l.radius, l.radius, l.r, l.g, l.b, l.alpha, k, l.marks, l.wave ? 1 : 0, l.glow ? 2 : 1);
    }
    this.zones.end();
  }

  get count(): number {
    return this.live.length + this.pieces.count;
  }

  /** Malhas dos efeitos na cena (o lote das zonas e um por forma de peça): os draw calls no pior caso. */
  get batchCount(): number {
    return 1 + this.pieces.batchCount;
  }

  /** Sai da partida: tira os efeitos vivos, os lotes e os materiais deles. */
  dispose(): void {
    this.pieces.dispose();
    this.zones.dispose();
    this.zoneMaterial.dispose();
    this.live.length = 0;
  }
}
